import { computed, Injectable, signal } from '@angular/core';
import {
  AuthBootstrapResponse,
  AuthSessionUser,
  AuthTokenResponse,
  PortalAccessDto,
} from './auth.types';

/** Convierte epoch en segundos a milisegundos cuando el API no envía ms. */
export function normalizeExpiresAt(expiresAt: number): number {
  if (!Number.isFinite(expiresAt) || expiresAt <= 0) return expiresAt;
  return expiresAt < 1_000_000_000_000 ? expiresAt * 1000 : expiresAt;
}

@Injectable({ providedIn: 'root' })
export class AuthSessionStore {
  private readonly tokenState = signal<AuthTokenResponse | null>(null);
  private readonly bootstrapState = signal<AuthBootstrapResponse | null>(null);

  readonly accessToken = computed(() => this.tokenState()?.accessToken ?? null);
  readonly expiresAt = computed(() => this.tokenState()?.expiresAt ?? 0);
  readonly bootstrap = this.bootstrapState.asReadonly();
  readonly systems = computed<readonly PortalAccessDto[]>(
    () =>
      [...(this.bootstrapState()?.systems ?? [])].sort(
        (left, right) => left.order - right.order,
      ),
  );
  readonly navigation = computed(() => this.bootstrapState()?.navigation ?? []);
  readonly permissions = computed(
    () => this.bootstrapState()?.permissions ?? [],
  );
  readonly currentUser = computed<AuthSessionUser | null>(() => {
    const token = this.tokenState();
    if (!token) return null;

    const bootstrap = this.bootstrapState();
    const user = bootstrap?.user ?? token.user;
    const role = bootstrap?.role;

    return {
      id: user.uuid,
      name: user.displayName?.trim() || user.email,
      email: user.email,
      role: {
        id: role?.code ?? '',
        name: role?.name ?? '',
      },
    };
  });
  isAuthenticated(): boolean {
    return Boolean(this.accessToken()) && this.expiresAt() > Date.now();
  }

  setToken(token: AuthTokenResponse): void {
    this.tokenState.set({
      ...token,
      expiresAt: normalizeExpiresAt(token.expiresAt),
    });
  }

  setBootstrap(bootstrap: AuthBootstrapResponse): void {
    this.bootstrapState.set(bootstrap);
  }

  clear(): void {
    this.tokenState.set(null);
    this.bootstrapState.set(null);
  }
}
